import {
  getNodeDataRecord,
  getValueNodeBounds,
  getValueNodeComponentCount,
  getValueNodeOutputType,
  getValueNodeSubtype,
  getValueSubtypeLabel,
  isValueNode,
} from '../graph/nodeDefs.js';
import type { GraphNode, ValuePortType, ValueSubtype } from '../graph/nodeDefs.js';
import { hexToRgb, rgbToHex } from '../math/color.js';
import { createDragger, type DragFieldElement } from './controls/dragField.js';
import { createColorControl, createEditableColorField } from './colorField.js';
import {
  createNodeFieldRow as createRow,
  createNodeFieldSelect as createSelect,
  createNodeFieldTextInput as createTextInput,
  createNodeValueLabel,
  createValueSectionRow,
} from './nodeFieldEditorControls.js';
import type { NodeFieldRenderDeps as RenderDeps } from './nodeRenderers.js';

const FLOAT_SUBTYPES: ValueSubtype[] = ['default', 'factor', 'angle', 'distance', 'pixels', 'percentage', 'em'];
const VECTOR_SUBTYPES: ValueSubtype[] = ['default', 'color'];

function allowedSubtypes(type: ValuePortType): ValueSubtype[] {
  if (type === 'float') return FLOAT_SUBTYPES;
  if (type === 'vec3' || type === 'vec4') return VECTOR_SUBTYPES;
  return ['default'];
}

function ensureSubtypeForType(type: ValuePortType, subtype: ValueSubtype): ValueSubtype {
  const allowed = allowedSubtypes(type);
  return allowed.includes(subtype) ? subtype : 'default';
}

export function renderValueNodeUI(node: GraphNode, container: HTMLElement, deps: RenderDeps): void {
  if (!isValueNode(node)) return;

  const type = getValueNodeOutputType(node) as ValuePortType;
  const subtype = ensureSubtypeForType(type, getValueNodeSubtype(node));
  const numComps = getValueNodeComponentCount(type);
  const nodeData = getNodeDataRecord(node);
  const supportsColor = type === 'vec3' || type === 'vec4';
  const renderAsColor = supportsColor && subtype === 'color';
  const valueLabelText = getValueSubtypeLabel(subtype);
  const sectionLabelText = node.data.exposed ? 'Default' : 'Value';
  const { min: dragMin, max: dragMax, hasSlider } = getValueNodeBounds(node.data);

  const optionsRow = createRow();
  optionsRow.classList.add('value-row--toggle');

  const exposeLabel = document.createElement('label');
  exposeLabel.className = 'value-toggle';

  const exposeToggle = document.createElement('input');
  exposeToggle.type = 'checkbox';
  exposeToggle.checked = Boolean(node.data.exposed);
  exposeToggle.addEventListener('mousedown', e => e.stopPropagation());
  exposeToggle.addEventListener('change', () => {
    deps.commands.setValueExposed(node.id, exposeToggle.checked);
    deps.updateParams();
    deps.renderGraph();
    deps.scheduleRender();
  });

  const exposeText = document.createElement('span');
  exposeText.textContent = 'Expose';
  exposeLabel.appendChild(exposeToggle);
  exposeLabel.appendChild(exposeText);
  optionsRow.appendChild(exposeLabel);

  container.appendChild(optionsRow);

  const nameRow = createRow();
  nameRow.appendChild(createNodeValueLabel('Name'));
  nameRow.appendChild(createTextInput(String(node.data.name ?? ''), value => {
    deps.commands.setValueName(node.id, value);
    deps.updateParams();
    deps.scheduleRender();
  }));
  container.appendChild(nameRow);

  const typeRow = createRow();
  typeRow.appendChild(createNodeValueLabel('Type'));

  const typeSelect = createSelect('78px');
  (['float', 'vec2', 'vec3', 'vec4'] as const).forEach(optionType => {
    const option = document.createElement('option');
    option.value = optionType;
    option.textContent = optionType;
    option.selected = optionType === type;
    typeSelect.appendChild(option);
  });
  typeSelect.addEventListener('change', () => {
    const nextType = typeSelect.value as ValuePortType;
    const nextSubtype = ensureSubtypeForType(nextType, subtype);
    deps.commands.setValueOutputType(node.id, nextType);
    if (nextSubtype !== subtype) {
      deps.commands.setValueSubtype(node.id, nextSubtype);
    }
    deps.renderGraph();
    deps.scheduleRender();
    deps.updateParams();
  });
  typeSelect.style.width = '100%';
  typeRow.appendChild(typeSelect);

  const outDot = document.createElement('div');
  outDot.className = `port-dot out type-${type}`;
  outDot.dataset.node = node.id;
  outDot.dataset.port = '0';
  outDot.dataset.io = 'out';
  typeRow.appendChild(outDot);
  deps.setupPortDrag(outDot);
  container.appendChild(typeRow);

  const subtypeOptions = allowedSubtypes(type);
  if (subtypeOptions.length > 1) {
    const subtypeRow = createRow();
    subtypeRow.appendChild(createNodeValueLabel('Subtype'));

    const subtypeSelect = createSelect('112px');
    subtypeOptions.forEach(optionSubtype => {
      const option = document.createElement('option');
      option.value = optionSubtype;
      option.textContent = optionSubtype;
      option.selected = optionSubtype === subtype;
      subtypeSelect.appendChild(option);
    });
    subtypeSelect.addEventListener('change', () => {
      deps.commands.setValueSubtype(node.id, subtypeSelect.value as ValueSubtype);
      deps.updateParams();
      deps.renderGraph();
      deps.scheduleRender();
    });
    subtypeSelect.style.width = '100%';
    subtypeRow.appendChild(subtypeSelect);
    container.appendChild(subtypeRow);
  }

  container.appendChild(createValueSectionRow(sectionLabelText));

  if (renderAsColor) {
    const colorRow = createRow();
    const colorValue = rgbToHex(
      Number(nodeData.value0 ?? 0),
      Number(nodeData.value1 ?? 0),
      Number(nodeData.value2 ?? 0),
    );
    let alphaInput: DragFieldElement | undefined;
    const colorField = createEditableColorField({
      value: colorValue,
      opacity: type === 'vec4' ? Number(nodeData.value3 ?? 1) : undefined,
      classNames: {
        field: 'value-color-field',
        swatchButton: 'value-color-swatch-button',
        swatch: 'value-color-swatch',
        valueInput: 'value-color-value',
      },
      inputAriaLabel: 'Hex color',
      stopMouseDownPropagation: true,
      onInputValue: value => {
        const [red, green, blue] = hexToRgb(value);
        deps.commands.setValueComponent(node.id, 0, red);
        deps.commands.setValueComponent(node.id, 1, green);
        deps.commands.setValueComponent(node.id, 2, blue);
        deps.updateParams();
        deps.scheduleRender();
      },
      onOpacityInput: type === 'vec4' ? value => {
        deps.commands.setValueComponent(node.id, 3, value);
        alphaInput?.setValue(value);
        deps.updateParams();
        deps.scheduleRender();
      } : undefined,
    });
    let alphaControl: HTMLElement | undefined;

    if (type === 'vec4') {
      alphaInput = createDragger(
        Number(nodeData.value3 ?? 1),
        0,
        1,
        0.01,
        value => {
          deps.commands.setValueComponent(node.id, 3, value);
          colorField.setOpacity(value);
          deps.updateParams();
          deps.scheduleRender();
        },
        undefined,
        { subtype: 'percentage' },
      );
      alphaInput.style.width = '56px';
      alphaControl = alphaInput;
    }

    colorRow.appendChild(createColorControl({
      classNames: {
        container: 'value-inline-control value-inline-control--row',
        leading: 'value-inline-control value-inline-control--leading',
        label: 'value-channel-label',
        trailing: 'value-inline-control value-inline-control--alpha',
      },
      labelText: 'Color',
      colorField,
      trailingControl: alphaControl,
    }));
    container.appendChild(colorRow);
  } else {
    const componentLabels = ['X', 'Y', 'Z', 'W'];
    for (let index = 0; index < numComps; index++) {
      const valueRow = createRow();
      valueRow.appendChild(createNodeValueLabel(numComps === 1 ? valueLabelText : componentLabels[index] ?? ''));
      const input = createDragger(
        Number(nodeData[`value${index}`] ?? 0),
        type === 'float' ? dragMin : -1000,
        type === 'float' ? dragMax : 1000,
        0.01,
        value => {
          deps.commands.setValueComponent(node.id, index as 0 | 1 | 2 | 3, value);
          deps.updateParams();
          deps.scheduleRender();
        },
        undefined,
        {
          showProgress: type === 'float' && index === 0 && hasSlider,
          subtype: subtype === 'default' ? undefined : subtype,
        },
      );
      input.style.width = '100%';
      valueRow.appendChild(input);
      container.appendChild(valueRow);
    }
  }

  if (type === 'float') {
    ([
      ['min', 'Min'],
      ['max', 'Max'],
    ] as const).forEach(([field, labelText]) => {
      const rangeRow = createRow();
      const label = document.createElement('label');
      label.className = 'value-range-label';

      const toggle = document.createElement('input');
      toggle.type = 'checkbox';
      toggle.checked = field === 'min' ? Boolean(node.data.hasMin) : Boolean(node.data.hasMax);
      toggle.addEventListener('mousedown', e => e.stopPropagation());
      toggle.addEventListener('change', () => {
        deps.commands.setValueRangeEnabled(node.id, field, toggle.checked);
        deps.updateParams();
        deps.renderGraph();
        deps.scheduleRender();
      });

      const text = document.createElement('span');
      text.className = 'value-range-text';
      text.textContent = labelText;

      label.appendChild(toggle);
      label.appendChild(text);
      rangeRow.appendChild(label);

      const input = createDragger(
        Number(node.data[field]),
        Number.NEGATIVE_INFINITY,
        Number.POSITIVE_INFINITY,
        0.01,
        value => {
          deps.commands.setValueRangeField(node.id, field, value);
          deps.updateParams();
          deps.scheduleRender();
        },
        undefined,
        { subtype: subtype === 'default' ? undefined : subtype },
      );
      input.style.width = '100%';
      rangeRow.appendChild(input);
      container.appendChild(rangeRow);
    });
  }
}
